import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminAxios, auth, formatApiError } from "@/lib/admin_api";
import { toast } from "sonner";
import {
  LogOut,
  LayoutDashboard,
  Mail,
  Phone,
  Building2,
  Calendar,
  Inbox,
} from "lucide-react";

function downloadCsv(filename, rows, headers) {
  if (!rows.length) return;
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const HEADERS = ["created_at", "name", "email", "phone", "company", "service", "message"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(true);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const { data } = await adminAxios.get("/admin/contacts");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!auth.getToken()) {
      navigate("/admin");
      return;
    }
    adminAxios
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        setChecking(false);
        loadInbox();
      })
      .catch(() => {
        auth.clear();
        navigate("/admin");
      });
  }, [navigate]);

  const logout = () => {
    auth.clear();
    toast.success("Logged out.");
    navigate("/admin");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground text-sm">
        Checking session…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-accent flex items-center justify-center text-accent-foreground">
              <LayoutDashboard size={16} strokeWidth={1.8} />
            </span>
            <div className="leading-tight">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-accent">
                Admin
              </p>
              <p className="font-heading text-lg font-semibold">Gamya inbox</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-sm opacity-70">
              {user?.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 text-sm"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-8 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-medium text-primary tracking-tight">
              Contact inquiries
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              All submissions from your website's contact form.
            </p>
          </div>
          <button
            onClick={() => downloadCsv(`gamya-inquiries-${new Date().toISOString().slice(0,10)}.csv`, items, HEADERS)}
            disabled={!items.length}
            className="px-4 h-10 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium disabled:opacity-50"
            data-testid="admin-download"
          >
            Download CSV ({items.length})
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-14 text-center">
            <Inbox size={28} className="mx-auto text-muted-foreground/60" />
            <p className="mt-3 text-muted-foreground">
              No inquiries yet. They'll appear here as soon as someone submits the contact form.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it, idx) => (
              <div
                key={it.id || idx}
                className="bg-surface rounded-xl border border-border p-5"
                data-testid={`admin-inbox-row-${idx}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-primary">
                      {it.name}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail size={12} /> {it.email}
                      </span>
                      {it.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {it.phone}
                        </span>
                      )}
                      {it.company && (
                        <span className="flex items-center gap-1">
                          <Building2 size={12} /> {it.company}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />{" "}
                        {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  </div>
                  {it.service && (
                    <span className="text-[10px] tracking-[0.16em] uppercase font-bold text-accent bg-accent/10 px-2.5 py-1 rounded-full">
                      {it.service}
                    </span>
                  )}
                </div>
                {it.message && (
                  <p className="mt-3 text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                    {it.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
